import { Alert, Box, Container, Paper, Typography } from '@mui/material';
import { useEffect, useState, FC } from 'react';
import { createSearchParams, useNavigate } from 'react-router';
import PaymentVendors from '@acorn-portal/shared/src/components/vendor/PaymentVendors';
import SimpleBraintreePaymentForm from '../../components/payment/simple-braintree-payment-form';
import {
	MakePaymentResponseModel,
	PaymentDetailsByTokenModel,
	PayNowStatus,
	PayNowClient,
} from '@acorn-portal/shared/src/services/api';
import PaymentHeader from '@acorn-portal/shared/src/components/header/PaymentHeader';
import { formatCurrency } from '@acorn-portal/shared/src/utils/formatter';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { CustomPaymentMethodPayload } from '@acorn-portal/shared/src/utils/types/extensions/braintree';
import { logErrorToSentry } from '@acorn-portal/shared/src/components/sentry/Integration';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { updatePayNowDetails } from '@acorn-portal/shared/src/store/paynow.slice';
import FullPageLoadingSplash from '@acorn-portal/shared/src/components/loading/FullPageLoadingSplash';
import { isAxiosError } from 'axios';

type PaymentDetails = Omit<PaymentDetailsByTokenModel, 'validUntil'> & {
	validUntil?: string;
};

const DirectPayment: FC = () => {
	const payNowDetails = useAppSelector((redux) => redux.payNow);
	const dispatch = useAppDispatch();
	const [errorMessage, setErrorMessage] = useState<string>();
	const [loading, setLoading] = useState(false);
	const [initialLoading, setInitialLoading] = useState(false);
	const [paymentToken, setPaymentToken] = useState('');
	const [paymentDetails, setPaymentDetails] = useState<PaymentDetails>();
	const navigate = useNavigate();
	const isConsolidatedPaymentSchedule = payNowDetails.details?.status === PayNowStatus.ConsolidatedPaymentSchedule;

	const onDirectPayment = async (payload: CustomPaymentMethodPayload) => {
		if (!paymentDetails) {
			setErrorMessage('Unable to get your payment details.');
			return;
		}

		const { nonce, threeDSecureInfo, deviceData } = payload;
		const { amount, splitPaymentId, directDebitId, emailAddress } = paymentDetails;
		const { missingPaymentFormDetails } = payNowDetails;
		const params = new URLSearchParams(window.location.search);
		const token = params.get('token');
		const isInitialPayment = params.get('isInitialPayment');

		try {
			const client = new PayNowClient(undefined, API);
			let makePaymentResponse: MakePaymentResponseModel | undefined;

			if (isInitialPayment && token) {
				const vaultCard = missingPaymentFormDetails ? !missingPaymentFormDetails.vaultCardOptOut : false;

				const paymentPlanType = missingPaymentFormDetails.splitPaymentPlanType;

				makePaymentResponse = await client.makeTokenInitialPayment(token, {
					paymentNonce: nonce,
					deviceData: deviceData ?? '',
					amount,
					directDebitId,
					threeDSecureAuthenticationId: threeDSecureInfo ? threeDSecureInfo.threeDSecureAuthenticationId : '',
					vaultCard,
					emailAddress,
					paymentPlanType,
				});
			} else if (directDebitId) {
				makePaymentResponse = await client.makeDirectDebitPayment({
					paymentNonce: nonce,
					deviceData: deviceData ?? '',
					amount,
					directDebitId,
					threeDSecureAuthenticationId: threeDSecureInfo ? threeDSecureInfo.threeDSecureAuthenticationId : '',
					token: token ?? undefined,
					vaultCard: true,
				});
			} else {
				makePaymentResponse = await client.makeDirectPayment({
					paymentNonce: nonce,
					deviceData: deviceData ?? '',
					amount,
					splitPaymentId,
					threeDSecureAuthenticationId: threeDSecureInfo ? threeDSecureInfo.threeDSecureAuthenticationId : '',
					token: token ?? undefined,
				});
			}

			if (!makePaymentResponse.isSuccessful) {
				setErrorMessage('Unable to process payment, please contact our web chat team.');
				return;
			}

			if (payNowDetails.details) {
				dispatch(
					updatePayNowDetails({
						...payNowDetails,
						details: {
							...payNowDetails.details,
							status: PayNowStatus.ActivePaymentSchedule,
						},
					}),
				);
			}
			navigate(ROUTE_PATHS.payNowDirectPaymentSuccess, {
				state: {
					amount,
				},
			});
		} catch (e) {
			logErrorToSentry(e);
			setErrorMessage('Unable to process payment, please contact our web chat team.');
		}
	};
	// TODO: Sort this mess out
	useEffect(() => {
		const getPaymentToken = async () => {
			setLoading(true);
			try {
				const client = new PayNowClient(undefined, API);
				const token = await client.generatePaymentToken(undefined);
				setPaymentToken(token);
			} catch (e) {
				logErrorToSentry(e);
				navigate(ROUTE_PATHS.unauthorisedError, { replace: true });
			} finally {
				setLoading(false);
			}
		};
		const getPaymentDetailsByToken = async (token: string) => {
			try {
				setInitialLoading(true);
				const client = new PayNowClient(undefined, API);
				const paymentDetailsResponse = await client.getPaymentDetailsByToken(token);
				const ValidUntil = paymentDetailsResponse.validUntil;
				const currentDate = new Date();
				const validUntilDate = new Date(ValidUntil);
				if (validUntilDate < currentDate) {
					navigate(ROUTE_PATHS.expiredPaymentLink, { replace: true });
					return;
				}
				setPaymentDetails(paymentDetailsResponse);
				await getPaymentToken();
				setInitialLoading(false);
			} catch (e) {
				setInitialLoading(false);
				logErrorToSentry(e);
				if (isAxiosError(e) && e.response?.status === 404) {
					navigate(ROUTE_PATHS.invalidPaymentLink, { replace: true });
				} else {
					navigate(ROUTE_PATHS.unauthorisedError, { replace: true });
				}
			}
		};

		const params = new URLSearchParams(window.location.search);
		const token = params.get('token');
		const isInitialPayment = params.get('isInitialPayment');

		if (token !== null && isInitialPayment === null) {
			getPaymentDetailsByToken(token);
			return;
		}

		if (token && isInitialPayment && !payNowDetails.details?.agreementId) {
			navigate({
				pathname: ROUTE_PATHS.payNowDirectAgreement,
				search: `?${createSearchParams({
					token,
					isInitialPayment: '1',
				})}`,
			});
		}

		if (
			payNowDetails.details &&
			(payNowDetails.details.overDuePaymentPlan?.amount || payNowDetails.details.splitPaymentPlans[0]?.amount)
		) {
			setPaymentDetails({
				amount: isConsolidatedPaymentSchedule
					? payNowDetails.details.splitPaymentPlans[0].amount
					: payNowDetails.details.overDuePaymentPlan?.amount || 0,
				splitPaymentId: isConsolidatedPaymentSchedule
					? payNowDetails.details.splitPaymentPlans[0].splitPaymentId
					: payNowDetails.details.overDuePaymentPlan?.splitPaymentId || '',
				customerId: payNowDetails.details.customerId || '',
				agreementId: payNowDetails.details.agreementId || '',
				emailAddress: payNowDetails.details.email || '',
				directDebitId: payNowDetails.details.directDebitId || undefined,
				validUntil: payNowDetails.details.validUntil || '',
			});

			getPaymentToken();
			return;
		}
		navigate('/', { replace: true });
	}, []);

	return (
		<FullPageLoadingSplash loading={initialLoading} message='Retrieving Payment Details...'>
			<Paper
				elevation={1}
				sx={{
					margin: 1.5,
					borderRadius: '8px',
					paddingBottom: 3,
				}}
			>
				<Container maxWidth='sm'>
					<Box sx={{ padding: 1 }} />
					<Box my={4}>
						<PaymentHeader title='Secure Payment Checkout' />
						<Typography textAlign='left' sx={{ fontSize: '20px' }}>
							Payment to be taken : <b>{paymentDetails?.amount ? formatCurrency(paymentDetails?.amount) : ''}</b>
						</Typography>
						<Typography textAlign='left'>You will receive an email as receipt and confirmation of payment.</Typography>
						<SimpleBraintreePaymentForm
							loading={loading}
							amount={paymentDetails?.amount?.toString()}
							clientToken={paymentToken}
							submitPayment={onDirectPayment}
							isOptOut={false}
							setErrorMessage={(value) => setErrorMessage(value)}
						/>
						{errorMessage && (
							<Alert severity='error' sx={{ width: '100%', mt: 2 }}>
								{errorMessage}
							</Alert>
						)}
						<PaymentVendors />
					</Box>
				</Container>
			</Paper>
		</FullPageLoadingSplash>
	);
};
export default DirectPayment;
